import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import BaseModal from '@/modal/baseModal';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  acceptTermsAndConditions,
  dismissTermsAndConditionsModal,
  selectTermsAndConditions,
  resetTermsAndConditionsState,
} from '@/features/termsAndConditions/termsAndConditionsSlice';
import { signOutScope } from '@/utils/session';
import styles from './styles.module.scss';
import Button from '@/ui-kit/Button';
import SubmitButton from '@/ui-kit/SubmitButton';
import ExitConfirmationModal from './ExitConfirmationModal';

export const GeneralConditionsModal: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { termsId, latestVersion, publishedAt, termsContents, loading, error, isMandatory } = useAppSelector(selectTermsAndConditions);

  const [isAccepted, setIsAccepted] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  const currentContent = useMemo(() => {
    if (!termsContents || termsContents.length === 0) return '';
    const currentLanguage = i18n.language?.substring(0, 2).toUpperCase() || 'EN';
    const content = termsContents.find(tc => tc.language.toUpperCase() === currentLanguage);
    if (content) {
      return content.content;
    }
    const englishContent = termsContents.find(tc => tc.language.toUpperCase() === 'EN');
    return englishContent?.content || '';
  }, [termsContents, i18n.language]);

  const formattedDate = useMemo(() => {
    if (!publishedAt) return '';
    try {
      const date = new Date(publishedAt);
      return date.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return publishedAt;
    }
  }, [publishedAt, i18n.language]);

  const handleValidate = async () => {
    if (!isAccepted || !termsId || !currentContent) return;
    await dispatch(acceptTermsAndConditions(termsId));
  };

  const handleExit = () => {
    setShowExitConfirmation(true);
  };

  const handleDismiss = () => {
    dispatch(dismissTermsAndConditionsModal());
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
  };

  const handleConfirmExit = async () => {
    dispatch(resetTermsAndConditionsState());
    try {
      await signOutScope();
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/log-in');
  };

  const renderContent = () => {
    if (!currentContent) {
      return <p>{t('general.loading')}</p>;
    }
    return <ReactMarkdown>{currentContent}</ReactMarkdown>;
  };

  if (showExitConfirmation) {
    return <ExitConfirmationModal onCancel={handleCancelExit} onConfirmExit={handleConfirmExit} />;
  }

  return (
    <BaseModal width='800px' hideCloseIcon={true}>
      <div className={styles.modal__header}>
        <h6 id='termsAndConditionsTitle' className={styles.modal__title}>
          {t('termsAndConditions.title')}
        </h6>
        <span id='termsAndConditionsVersion' className={styles.modal__version}>
          {t('termsAndConditions.versionLabel', { version: latestVersion, date: formattedDate })}
        </span>
      </div>

      <div id='termsAndConditionsContent' className={styles.modal__content}>
        {renderContent()}
      </div>

      {error && <div className={styles.modal__error}>{t('termsAndConditions.acceptError', { defaultValue: error })}</div>}

      <div className={styles.modal__checkbox}>
        <input
          type='checkbox'
          id='termsAndConditions-accept-checkbox'
          checked={isAccepted}
          onChange={e => setIsAccepted(e.target.checked)}
        />
        <label htmlFor='termsAndConditions-accept-checkbox'>{t('termsAndConditions.confirmCheckbox')}</label>
      </div>

      <div className={styles.modal__actions}>
        {isMandatory ? (
          <Button id='exitTermsAndConditionsButton' label={t('termsAndConditions.exitButton')} onClick={handleExit} />
        ) : (
          <Button id='dismissTermsAndConditionsButton' label={t('termsAndConditions.dismissButton')} onClick={handleDismiss} />
        )}
        <SubmitButton
          id='validateTermsAndConditionsButton'
          label={t('termsAndConditions.validateButton')}
          onClick={handleValidate}
          disabled={!isAccepted || !termsId || !currentContent}
          loading={loading}
          inputStyle='w-max'
          paymentBtn={true}
        />
      </div>
    </BaseModal>
  );
};

export default GeneralConditionsModal;
